/* ============================================================
   The Cambie — teaser landing page
   Email capture -> Klaviyo client subscriptions endpoint.
   Config is read from data attributes on the section root so
   list IDs can be changed in the theme editor without touching JS.
   ============================================================ */

(function () {
  'use strict';

  var REVISION = '2024-10-15';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;

  /* ---- UTM / referral capture (page level, read once) ---- */
  function collectProperties(pageTag) {
    var q = new URLSearchParams(window.location.search);
    var props = {
      cambie_signup_page: pageTag || 'cambie-teaser-shopify',
      cambie_signup_at: new Date().toISOString()
    };
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (k) {
      var v = q.get(k);
      if (v) props[k] = v;
    });
    if (document.referrer) props.cambie_signup_referrer = document.referrer;
    return props;
  }

  /* ---- Klaviyo subscribe ---- */
  function subscribe(companyId, listId, email, properties, source) {
    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: { 'content-type': 'application/json', revision: REVISION },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: source,
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email, properties: properties }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: listId } } }
        }
      })
    }).then(function (res) {
      if (!res.ok) throw new Error('Klaviyo ' + res.status);
      return true;
    });
  }

  /* ---- Countdown ---- */
  function startCountdown(root) {
    var el = root.querySelector('[data-cambie-count]');
    if (!el) return;
    var iso = root.getAttribute('data-cambie-launch');
    var target = iso ? new Date(iso) : null;
    if (!target || isNaN(target.getTime())) {
      el.textContent = '';
      return;
    }

    function tick() {
      var diff = target - new Date();
      if (diff <= 0) {
        el.textContent = 'now';
        clearInterval(timer);
        return;
      }
      var d = Math.floor(diff / 86400000);
      var h = Math.floor((diff % 86400000) / 3600000);
      var m = Math.floor((diff % 3600000) / 60000);
      var s = Math.floor((diff % 60000) / 1000);
      el.textContent = d > 0 ? d + 'd ' + h + 'h ' + m + 'm' : h + 'h ' + m + 'm ' + s + 's';
    }

    tick();
    var timer = setInterval(tick, 1000);
    root.cambieTimer = timer;
  }

  /* ---- Scroll reveal ---- */
  function startReveal(root) {
    var targets = root.querySelectorAll('[data-cambie-reveal]');
    if (!targets.length) return;
    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(targets, function (t) {
        t.classList.add('is-in');
      });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );
    Array.prototype.forEach.call(targets, function (t) {
      io.observe(t);
    });
  }

  /* ---- Form wiring ---- */
  function wireForms(root) {
    var companyId = root.getAttribute('data-cambie-company');
    var primary = root.getAttribute('data-cambie-list-primary');
    var secondary = root.getAttribute('data-cambie-list-secondary');
    var source = root.getAttribute('data-cambie-source') || 'Cambie Teaser Landing Page';
    var pageTag = root.getAttribute('data-cambie-page');
    var successCopy = root.getAttribute('data-cambie-success') || "You're on the list.";
    var properties = collectProperties(pageTag);

    root.querySelectorAll('[data-cambie-signup]').forEach(function (form) {
      if (form.cambieWired) return;
      form.cambieWired = true;

      var input = form.querySelector('input[type="email"]');
      var msg = form.querySelector('[data-cambie-msg]');
      var label = form.querySelector('[data-cambie-label]');
      var original = label ? label.textContent : '';

      function say(text, state) {
        if (!msg) return;
        msg.textContent = text;
        if (state) msg.setAttribute('data-state', state);
        else msg.removeAttribute('data-state');
      }

      input.addEventListener('input', function () {
        input.removeAttribute('aria-invalid');
        if (msg && msg.getAttribute('data-state') === 'error') say('');
      });

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (form.classList.contains('is-busy') || form.classList.contains('is-done')) return;

        var email = input.value.trim();
        if (!EMAIL_RE.test(email)) {
          input.setAttribute('aria-invalid', 'true');
          say('Please enter a valid email address.', 'error');
          input.focus();
          return;
        }

        if (!companyId || !primary) {
          say('Sign-up is not configured yet. Please try again later.', 'error');
          return;
        }

        form.classList.add('is-busy');
        if (label) label.textContent = 'Joining…';
        say('');

        // Primary list must succeed; the newsletter add is best-effort.
        subscribe(companyId, primary, email, properties, source)
          .then(function () {
            if (secondary) {
              subscribe(companyId, secondary, email, properties, source).catch(function () {});
            }
            form.classList.remove('is-busy');
            form.classList.add('is-done');
            say(successCopy, 'ok');
            if (window.dataLayer) {
              window.dataLayer.push({ event: 'cambie_presale_signup', cambie_page: pageTag });
            }
          })
          .catch(function () {
            form.classList.remove('is-busy');
            if (label) label.textContent = original;
            say('Something went wrong on our end. Please try again in a moment.', 'error');
          });
      });
    });
  }

  function init(root) {
    if (!root) return;
    wireForms(root);
    startCountdown(root);
    startReveal(root);
  }

  function boot() {
    document.querySelectorAll('[data-cambie-lp]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-init when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-cambie-lp]');
    if (root) init(root);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-cambie-lp]');
    if (root && root.cambieTimer) clearInterval(root.cambieTimer);
  });
})();
